import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import Loading from '../../../../components/Loading';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { Table } from '../../../../styles/table/Table';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { CtaButton, SmallButton } from '../../../../styles/ui/Button';
import {
   listOrigins,
   updateOrigin,
} from '../../../../services/hr/recruitment/originsApi';

import StatusBadge from '../components/StatusBadge';
import { recruitmentErrorMessage } from '../utils/apiError';
import OriginFormModal from './components/OriginFormModal';

// ---------------------------------------------------------------------------
// RH · Recrutamento — Catálogo de origens de candidatura (handoff 0061 §4.1 /
// §5.4). CRUD simples, SEM paginação (lista curta) — carrega tudo no mount (é um
// catálogo de gestão, não uma tela de pesquisa). Origens se INATIVAM em vez de
// excluir. Escrita exige hr:recruitment_catalog:manage.
// ---------------------------------------------------------------------------

export default function OriginsCatalog() {
   const { hasPermission } = usePermissions();
   const canManage = hasPermission('hr:recruitment_catalog:manage');

   const [origins, setOrigins] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [busyId, setBusyId] = useState(null);

   const [formOpen, setFormOpen] = useState(false);
   const [editOrigin, setEditOrigin] = useState(null);

   const fetchOrigins = useCallback(async () => {
      setIsLoading(true);
      try {
         // Sem filtro `active` → traz ativas E inativas (catálogo completo).
         const list = await listOrigins({});
         setOrigins(list);
      } catch (err) {
         toast.error(recruitmentErrorMessage(err, 'Erro ao carregar as origens.'));
      } finally {
         setIsLoading(false);
      }
   }, []);

   useEffect(() => {
      fetchOrigins();
   }, [fetchOrigins]);

   const openCreate = () => {
      setEditOrigin(null);
      setFormOpen(true);
   };

   const openEdit = (origin) => {
      setEditOrigin(origin);
      setFormOpen(true);
   };

   const handleToggleActive = async (origin) => {
      if (busyId) return;
      setBusyId(origin.id);
      try {
         const { message } = await updateOrigin(origin.id, {
            is_active: !origin.is_active,
         });
         toast.success(message);
         fetchOrigins();
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao atualizar a origem.'),
         );
      } finally {
         setBusyId(null);
      }
   };

   const hasResults = origins.length > 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Origens de candidatura</PageTitle>
               <PageSubtitle>
                  Catálogo de &quot;de onde o candidato veio&quot;
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               {canManage && (
                  <CtaButton type="button" onClick={openCreate}>
                     + Nova origem
                  </CtaButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>
                  Catálogo{origins.length ? ` (${origins.length})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {hasResults ? (
                  <TableWrapper>
                     <Table>
                        <thead>
                           <tr>
                              <th>Nome</th>
                              <th className="center">Status</th>
                              {canManage && <th className="center">Ações</th>}
                           </tr>
                        </thead>
                        <tbody>
                           {origins.map((origin) => (
                              <tr key={origin.id}>
                                 <td>{origin.name}</td>
                                 <td className="center">
                                    <StatusBadge
                                       variant={
                                          origin.is_active ? 'green' : 'neutral'
                                       }
                                    >
                                       {origin.is_active ? 'Ativa' : 'Inativa'}
                                    </StatusBadge>
                                 </td>
                                 {canManage && (
                                    <td
                                       className="center"
                                       style={{ whiteSpace: 'nowrap' }}
                                    >
                                       <SmallButton
                                          type="button"
                                          onClick={() => openEdit(origin)}
                                          style={{ marginRight: 8 }}
                                       >
                                          Editar
                                       </SmallButton>
                                       <SmallButton
                                          type="button"
                                          $intent={
                                             origin.is_active
                                                ? 'danger'
                                                : 'write'
                                          }
                                          onClick={() =>
                                             handleToggleActive(origin)
                                          }
                                          disabled={busyId === origin.id}
                                       >
                                          {origin.is_active
                                             ? 'Inativar'
                                             : 'Ativar'}
                                       </SmallButton>
                                    </td>
                                 )}
                              </tr>
                           ))}
                        </tbody>
                     </Table>
                  </TableWrapper>
               ) : (
                  <EmptyState
                     title="Nenhuma origem cadastrada"
                     description="Cadastre a primeira origem de candidatura."
                  />
               )}
            </SectionBody>
         </Section>

         <OriginFormModal
            isOpen={formOpen}
            onClose={() => setFormOpen(false)}
            origin={editOrigin}
            onSaved={fetchOrigins}
         />
      </Container>
   );
}
